import { queryParameters, fetchJson } from 'util/fetch';
import {
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  CREATE,
  UPDATE,
  DELETE,
  GET_META,
  EXPORT
} from './types';
import { shopDisableBackendPath } from "routes/shop/urls";

import { reminderConfigBackendPath } from "routes/advancedFunction/urls";
import { convertVersionNumber } from "routes/advancedFunction/utils";

import dayjs from "dayjs";

/**
 * Maps admin-on-rest queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?time_from=2016-01-01
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?ids=[123,456,789]
 * UPDATE       => POST http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (apiUrl, httpClient = fetchJson) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} apiPath Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertRESTRequestToHTTP = (type, apiPath, params) => {
    let url = '';
    const options = {};
    switch (type) {
      case GET_LIST: {
        url = `${apiUrl}${apiPath}?${queryParameters(params)}`;
        break;
      }
      case GET_META:
        url = `${apiUrl}${apiPath}?${queryParameters(params)}`;
        break;
      case GET_ONE:
        url = `${apiUrl}${apiPath}${params.id}/`;
        break;
      case GET_MANY: {
        const query = {
          filter: JSON.stringify({ id: params.ids }),
        };
        url = `${apiUrl}${apiPath}?${queryParameters(query)}`;
        break;
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
          sort: JSON.stringify([field, order]),
          range: JSON.stringify([(page - 1) * perPage, (page * perPage) - 1]),
          filter: JSON.stringify({ ...params.filter, [params.target]: params.id }),
        };
        url = `${apiUrl}${apiPath}?${queryParameters(query)}`;
        break;
      }
      case UPDATE:
        url = `${apiUrl}${apiPath}${params.id}/`;
        options.method = 'POST';
        if (params.data instanceof FormData) {
          options.body = params.data;
        }
        else {
          options.body = JSON.stringify(params.data);
        }
        break;
      case CREATE:
        url = `${apiUrl}${apiPath}`;
        options.method = 'POST';
        if (params.data instanceof FormData) {
          options.body = params.data
        }
        else {
          options.body = JSON.stringify(params.data);
        }
        break;
      case DELETE:
        url = `${apiUrl}${apiPath}/${params.id}`;
        options.method = 'DELETE';
        break;
      case EXPORT:
        url = `${apiUrl}${apiPath}`;
        options.method = 'POST';
        options.body = JSON.stringify(params.data);
        break;
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return { url, options };
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {Object} params The REST request params, depending on the type
   * @param {String} apiPath
   * @returns {Object} REST response
   */
  const convertHTTPResponseToREST = (response, type, params, apiPath) => {
    const { json } = response;
    switch (type) {
      case GET_LIST:
      case GET_MANY_REFERENCE:
        const data = transformData(json.data, params, apiPath);

        // In case data return no id field, we need add it
        if (data.length > 0 && data[0].id == null ) {
          for (let i = 0; i < data.length; ++i) {
            data[i].id = i;
          }
        }

        return {
          data: data,
          total: data.length,
        };
      case CREATE:
        return { data: { ...params.data, ...json } };
      default:
        return { data: json };
    }
  };

  function transformData(data, params, apiPath) {
    let transformed_data = data;
    
    if (apiPath === shopDisableBackendPath) {
      transformed_data.sort((shop1, shop2) => {
        if (shop1.enabled === shop2.enabled) {
          const shop1CreatedDateStr = dayjs(shop1.created_at).format('YYYY-MM-DD');
          const shop2CreatedDateStr = dayjs(shop2.created_at).format('YYYY-MM-DD');
          if (shop1CreatedDateStr === shop2CreatedDateStr) {
            if (shop1.shop_name === shop2.shop_name) {
              return 0;
            } else {
              return shop1.shop_name > shop2.shop_name ? 1 : -1;
            }
          } else {
            return shop1CreatedDateStr > shop2CreatedDateStr ? 1 : -1;
          }
        } else {
          return shop1.enabled < shop2.enabled ? 1 : -1;
        }
      });
    } else if (apiPath === reminderConfigBackendPath) {
      transformed_data = data
        .filter((row) => ![513, 4097, 4353, 4355].includes(row.client_type))
        .map((row) => {
          const latest_version_info = row.latest_version_info || {
            version_number: '',
            changelog_en: '',
            changelog_th: '',
            changelog_vi: '',
          };
          const updated_latest_version_info = {
            ...(latest_version_info),
            changelog_en: latest_version_info.changelog_en.replace(/(\\r\\n|\\n|\\r)/gm, '\n'),
            changelog_th: latest_version_info.changelog_th.replace(/(\\r\\n|\\n|\\r)/gm, '\n'),
            changelog_vi: latest_version_info.changelog_vi.replace(/(\\r\\n|\\n|\\r)/gm, '\n'),
            version_number: latest_version_info.version_number ? convertVersionNumber(latest_version_info.version_number) : '',
          };

          return {
            ...row,
            internal_client_type: row.client_type + 1,
            latest_version_info: updated_latest_version_info,
            dropping_version_number: convertVersionNumber(row.dropping_version_number),
            dropping_version_last_support_day: row.dropping_version_last_support_day * 1000,
            min_version_number: convertVersionNumber(row.min_version_number),
          };
        });
    }

    return transformed_data;
  }

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a REST response
   */
  return (type, apiPath, params) => {
    const { url, options } = convertRESTRequestToHTTP(type, apiPath, params);
    return httpClient(url, options)
      .then(response => convertHTTPResponseToREST(response, type, params, apiPath));
  };
};



// WEBPACK FOOTER //
// ./src/api/restClient.js